import { supabase } from "@/integrations/supabase/client";

export interface RedirectResult {
  found: boolean;
  to_path?: string;
  redirect_type?: number;
  source?: 'url' | 'article' | 'card';
}

/**
 * Master redirect resolution function
 * Checks all redirect sources in priority order:
 * 1. Custom URL redirects
 * 2. Article slug redirects
 * 3. Card slug redirects
 */
export async function resolveRedirect(path: string): Promise<RedirectResult> {
  // Normalize path (remove leading/trailing slashes)
  const normalizedPath = path.replace(/^\/+|\/+$/g, '');
  
  // Hardcoded structural redirects (exact matches only)
  if (normalizedPath === 'kort') {
    return {
      found: true,
      to_path: '/',
      redirect_type: 301,
      source: 'url'
    };
  }
  
  // Check custom URL redirects first
  const urlRedirect = await checkUrlRedirects(normalizedPath);
  if (urlRedirect.found) return urlRedirect;
  
  // Check if it's an article path (articles/*)
  if (normalizedPath.startsWith('artikler/')) {
    const slug = normalizedPath.replace('artikler/', '');
    const articleRedirect = await checkArticleRedirects(slug);
    if (articleRedirect.found) return articleRedirect;
  }
  
  // Check if it's a card path (kredittkort/*)
  if (normalizedPath.startsWith('kredittkort/')) {
    const slug = normalizedPath.replace('kredittkort/', '');
    const cardRedirect = await checkCardRedirects(slug);
    if (cardRedirect.found) return cardRedirect;
  }
  
  return { found: false };
}

/**
 * Check custom URL redirects table
 */
async function checkUrlRedirects(path: string): Promise<RedirectResult> {
  try {
    const { data, error } = await supabase
      .from('url_redirects')
      .select('to_path, redirect_type')
      .eq('from_path', path)
      .eq('is_active', true)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking URL redirects:', error);
      return { found: false };
    }
    
    if (data) {
      // Track hit asynchronously (fire and forget) - disabled to avoid permission issues
      // Hits will be tracked via GTM events instead
      
      return {
        found: true,
        to_path: data.to_path,
        redirect_type: data.redirect_type,
        source: 'url'
      };
    }
    
    return { found: false };
  } catch (error) {
    console.error('Exception checking URL redirects:', error);
    return { found: false };
  }
}

/**
 * Check article slug redirects
 */
async function checkArticleRedirects(slug: string): Promise<RedirectResult> {
  try {
    const { data, error } = await supabase
      .from('article_slug_redirects')
      .select('article_id, articles(slug)')
      .eq('old_slug', slug)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking article redirects:', error);
      return { found: false };
    }
    
    if (data?.articles) {
      return {
        found: true,
        to_path: `/ratgeber/${(data.articles as any).slug}`,
        redirect_type: 301,
        source: 'article'
      };
    }
    
    return { found: false };
  } catch (error) {
    console.error('Exception checking article redirects:', error);
    return { found: false };
  }
}

/**
 * Check card slug redirects
 */
async function checkCardRedirects(slug: string): Promise<RedirectResult> {
  try {
    const { data, error } = await supabase
      .from('card_slug_redirects')
      .select('card_id, cards(slug)')
      .eq('old_slug', slug)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking card redirects:', error);
      return { found: false };
    }
    
    if (data?.cards) {
      return {
        found: true,
        to_path: `/kreditkarten/${(data.cards as any).slug}`,
        redirect_type: 301,
        source: 'card'
      };
    }
    
    return { found: false };
  } catch (error) {
    console.error('Exception checking card redirects:', error);
    return { found: false };
  }
}

